// L'entretien d'accueil (lot 3.5) : le tableau de bord « Alfred vous connaît à X % », puis un thème question par
// question, et l'écran « Voici ce que j'ai retenu » où chaque ligne se décoche avant d'être enregistrée. Rien n'est
// écrit avant ce dernier écran. Chaque question peut être passée, sauf le prénom.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Actions } from '../App';
import { EnTeteRetour, Fenetre, Icone } from '../components';
import { type LigneRetenue, NonConnecte, type QuestionDuJour, type QuestionEntretien, type TableauEntretien, type ThemeEntretien, api } from '../lib/api';

const JOURS: [string, string][] = [['lu', 'L'], ['ma', 'M'], ['me', 'M'], ['je', 'J'], ['ve', 'V'], ['sa', 'S'], ['di', 'D']];
const NOMS_JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function versConnexion(e: unknown, actions: Actions) {
  if (e instanceof NonConnecte) window.location.hash = '#/connexion';
  else actions.toast({ texte: (e as Error).message, erreur: true });
}

// --- le tableau de bord -----------------------------------------------------------------------------------------------

export function Connaissance({ actions }: { actions: Actions }) {
  const [t, setT] = useState<TableauEntretien | null>(null);
  useEffect(() => {
    api.entretien().then(setT).catch((e: unknown) => versConnexion(e, actions));
  }, [actions]);

  return (
    <main className="ecran">
      <EnTeteRetour titre="Faisons connaissance" retour="#/reglages" />
      {!t && <p className="message muted">Chargement…</p>}
      {t && t.score === 0 && (
        <Fenetre>
          <p style={{ margin: 0, fontWeight: 600 }}>Bonjour, je suis {t.assistant}.</p>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            Pour vous être utile, j’ai besoin de vous connaître un peu. Je vais vous poser quelques questions, par thèmes de
            1 à 4 minutes. Vous pouvez passer n’importe quelle question et tout modifier ensuite.
          </p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Ce que vous me dites reste dans votre espace personnel. Ces réponses-ci ne passent pas par l’intelligence artificielle.
          </p>
        </Fenetre>
      )}
      {t && t.score > 0 && (
        <Fenetre>
          <p className="message" style={{ margin: 0 }}>{t.assistant} vous connaît à <strong>{t.score} %</strong></p>
          <div className="jauge"><div style={{ width: `${t.score}%` }} /></div>
        </Fenetre>
      )}
      {t?.suggestion && (
        <a className="btn principal large entretien-suggestion" href={`#/entretien/${t.suggestion.numero}`}>
          {t.score === 0 ? 'Commencer' : `${t.suggestion.texte} → ${t.themes.find((x) => x.numero === t.suggestion?.numero)?.duree} min`}
        </a>
      )}
      {t && (
        <div className="dossier-notes">
          {t.themes.map((x) => (
            x.disponible ? (
              <a key={x.numero} className="note-ligne" href={`#/entretien/${x.numero}`}>
                <span className="t">{x.titre}</span>
                <span className="mono muted">{x.couvert ? 'connu · compléter' : x.fait ? '✓ refaire' : `${x.duree} min`}</span>
              </a>
            ) : (
              <div key={x.numero} className="note-ligne muted">
                <span className="t">{x.titre}</span>
                <span className="mono">bientôt</span>
              </div>
            )
          ))}
        </div>
      )}
    </main>
  );
}

// --- une question ------------------------------------------------------------------------------------------------------

type Valeur = unknown;

interface Habitude {
  habitude: string;
  texte?: string;
  frequence: 'jour' | 'semaine';
  fois?: number;
  jours?: string[];
  moment: 'matin' | 'midi' | 'apres_travail' | 'soir' | 'heure';
  heure?: string;
}

const MOMENTS: [Habitude['moment'], string][] = [['matin', 'Matin'], ['midi', 'Midi'], ['apres_travail', 'Après le travail'], ['soir', 'Soir'], ['heure', 'Heure précise']];

/** HH:MM décalé de `minutes`, sur 24 h. */
function decaler(h: string, minutes: number): string {
  const [a, b] = h.split(':').map(Number);
  const t = (((a! * 60 + b! + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** La valeur de départ d'une question : ce qui est connu, sinon le défaut (éventuellement tiré d'une autre réponse). */
function depart(q: QuestionEntretien, reponses: Record<string, Valeur>): Valeur {
  if (q.prerempli !== null && q.prerempli !== undefined) return q.prerempli;
  const d = q.defaut;
  const base = typeof d === 'object' ? (reponses[d.depuis] as { semaine?: string } | undefined)?.semaine : undefined;
  const heure = typeof d === 'object' ? (base ? decaler(base, d.decalage) : undefined) : d;
  switch (q.type) {
    case 'texte': return heure ?? '';
    case 'texte_long': return '';
    case 'heure_variante': return { semaine: heure ?? '07:00', weekend: null };
    case 'jours': return { jours: ['lu', 'ma', 'me', 'je', 've'] };
    case 'horaires': return { de: '09:00', a: '18:00', teletravail: [] };
    case 'moment': return { actif: true, heure: heure ?? '08:00', ...(q.jours ? { jours: 'semaine' } : {}) };
    case 'plages': return [];
    case 'cases': return [];
    case 'nom_complet': return { prenom: '', nom: '' };
    case 'date_annuelle': return { jour: 0, mois: 0 };
    case 'date': return { date: '' };
    case 'habitudes':
    case 'animaux':
    case 'taches':
    case 'echeances': return [];
    default: return null;
  }
}

/** La réponse est-elle complète ? Sinon, « Suivant » attend. */
function complete(q: QuestionEntretien, v: Valeur): boolean {
  switch (q.type) {
    case 'texte': return typeof v === 'string' && v.trim().length > 0;
    case 'choix': {
      const c = v as { valeur?: string; texte?: string } | null;
      const o = q.options.find((x) => x.valeur === c?.valeur);
      return !!o && (!o.champ || !!c?.texte?.trim());
    }
    case 'jours': {
      const j = v as { jours?: string[]; valeur?: string };
      return !!j.valeur || !!j.jours?.length;
    }
    case 'plages': return (v as { jours: string[] }[]).every((p) => p.jours.length > 0);
    case 'nom_complet': return !!(v as { prenom: string }).prenom.trim();
    case 'date_annuelle': {
      const d = v as { inconnu?: boolean; jour?: number; mois?: number };
      return !!d.inconnu || (!!d.jour && !!d.mois);
    }
    case 'date': {
      const d = v as { aucune?: boolean; date?: string };
      return !!d.aucune || !!d.date;
    }
    case 'habitudes': return (v as Habitude[]).every((h) => (h.habitude !== 'autre' || !!h.texte?.trim()) && (h.frequence === 'jour' || !!h.fois || !!h.jours?.length) && (h.moment !== 'heure' || !!h.heure));
    case 'animaux': return (v as { nom: string; espece: string }[]).every((a) => a.nom.trim() && a.espece.trim());
    case 'taches': return (v as { tache: string; texte?: string; jours: string[] }[]).every((x) => x.jours.length > 0 && (x.tache !== 'autre' || !!x.texte?.trim()));
    case 'echeances': return (v as { echeance: string; texte?: string; mois: number }[]).every((x) => x.mois > 0 && (x.echeance !== 'autre' || !!x.texte?.trim()));
    default: return true;
  }
}

function ChoixJours({ valeur, changer }: { valeur: string[]; changer: (j: string[]) => void }) {
  return (
    <div className="jours-choix" role="group" aria-label="Jours">
      {JOURS.map(([j, l], i) => (
        <button key={j} type="button" aria-pressed={valeur.includes(j)} aria-label={NOMS_JOURS[i]}
          onClick={() => changer(valeur.includes(j) ? valeur.filter((x) => x !== j) : [...valeur, j])}>{l}</button>
      ))}
    </div>
  );
}

function Champ({ q, v, changer, vous, prenom }: { q: QuestionEntretien; v: Valeur; changer: (v: Valeur) => void; vous: boolean; prenom: string }) {
  const lib = (o: { libelle: string; libelle_tu?: string }) => (vous ? o.libelle : o.libelle_tu ?? o.libelle).replace('{prenom}', prenom);
  switch (q.type) {
    case 'texte':
      return <input className="saisie texte entretien-saisie" value={v as string} maxLength={120} onChange={(e) => changer(e.target.value)} autoFocus />;
    case 'texte_long':
      return <textarea className="saisie texte entretien-saisie" rows={4} value={v as string} maxLength={1000} onChange={(e) => changer(e.target.value)} />;
    case 'choix': {
      const c = (v ?? {}) as { valeur?: string; texte?: string; jour?: string };
      const choisi = q.options.find((o) => o.valeur === c.valeur);
      const saisie = choisi?.champ === 'nom' || choisi?.champ === 'texte';
      return (
        <div className="choix-mode" role="radiogroup">
          {q.options.map((o) => (
            <button key={o.valeur} type="button" className="btn" role="radio" aria-checked={c.valeur === o.valeur} onClick={() => changer({ valeur: o.valeur, ...(o.champ === 'nom' || o.champ === 'texte' ? { texte: c.texte ?? '' } : {}) })}>
              <b>{lib(o)}</b>
              {o.exemple && <small>{o.exemple}</small>}
            </button>
          ))}
          {choisi?.champ === 'jour' && (
            <>
              <span className="muted" style={{ fontSize: 13 }}>Un jour préféré ? (facultatif)</span>
              <ChoixJours valeur={c.jour ? [c.jour] : []} changer={(j) => {
                const jour = j.find((x) => x !== c.jour);
                changer({ valeur: c.valeur, ...(jour ? { jour } : {}) });
              }} />
            </>
          )}
          {saisie && (
            <input className="saisie texte entretien-saisie" placeholder={choisi.champ === 'nom' ? (vous ? 'Votre nom' : 'Ton nom') : 'Comment ?'} value={c.texte ?? ''} maxLength={80}
              onChange={(e) => changer({ valeur: c.valeur, texte: e.target.value })} autoFocus />
          )}
        </div>
      );
    }
    case 'cases': {
      const c = v as string[];
      return (
        <div className="choix-mode" role="group">
          {q.options.map((o) => (
            <button key={o.valeur} type="button" className="btn" role="checkbox" aria-checked={c.includes(o.valeur)} onClick={() => changer(c.includes(o.valeur) ? c.filter((x) => x !== o.valeur) : [...c, o.valeur])}>
              <b>{lib(o)}</b>
            </button>
          ))}
        </div>
      );
    }
    case 'heure_variante': {
      const h = v as { semaine: string; weekend: string | null };
      return (
        <div className="formulaire">
          <input className="saisie texte entretien-saisie" type="time" value={h.semaine} onChange={(e) => changer({ ...h, semaine: e.target.value })} aria-label="En semaine" />
          <label className="entretien-case">
            <input type="checkbox" checked={h.weekend !== null} onChange={(e) => changer({ ...h, weekend: e.target.checked ? h.semaine : null })} />
            Le week-end, c’est différent
          </label>
          {h.weekend !== null && <input className="saisie texte entretien-saisie" type="time" value={h.weekend} onChange={(e) => changer({ ...h, weekend: e.target.value })} aria-label="Le week-end" />}
        </div>
      );
    }
    case 'jours': {
      const j = v as { jours?: string[]; valeur?: string };
      return (
        <div className="formulaire">
          <ChoixJours valeur={j.jours ?? []} changer={(jours) => changer(jours.length ? { jours } : { jours: [] })} />
          <div className="choix-mode" role="radiogroup">
            {q.options.map((o) => (
              <button key={o.valeur} type="button" className="btn" role="radio" aria-checked={j.valeur === o.valeur} onClick={() => changer({ valeur: o.valeur })}><b>{lib(o)}</b></button>
            ))}
          </div>
        </div>
      );
    }
    case 'horaires': {
      const h = v as { de: string; a: string; teletravail: string[] };
      return (
        <div className="formulaire">
          <div className="boutons">
            <input className="saisie texte entretien-saisie" type="time" value={h.de} onChange={(e) => changer({ ...h, de: e.target.value })} aria-label="De" />
            <input className="saisie texte entretien-saisie" type="time" value={h.a} onChange={(e) => changer({ ...h, a: e.target.value })} aria-label="À" />
          </div>
          <span className="muted" style={{ fontSize: 13 }}>Télétravail :</span>
          <ChoixJours valeur={h.teletravail} changer={(teletravail) => changer({ ...h, teletravail })} />
        </div>
      );
    }
    case 'moment': {
      const m = v as { actif: boolean; heure?: string; jours?: string };
      return (
        <div className="formulaire">
          <div className="choix-mode" role="radiogroup">
            <button type="button" className="btn" role="radio" aria-checked={m.actif} onClick={() => changer({ ...m, actif: true, heure: m.heure ?? '08:00' })}><b>Oui</b></button>
            <button type="button" className="btn" role="radio" aria-checked={!m.actif} onClick={() => changer({ ...m, actif: false })}><b>Non</b></button>
          </div>
          {m.actif && <input className="saisie texte entretien-saisie" type="time" value={m.heure} onChange={(e) => changer({ ...m, heure: e.target.value })} aria-label="Heure" />}
          {m.actif && q.jours && (
            <div className="boutons">
              <button type="button" className={`btn${m.jours === 'semaine' ? ' principal' : ''}`} onClick={() => changer({ ...m, jours: 'semaine' })}>En semaine</button>
              <button type="button" className={`btn${m.jours === 'tous' ? ' principal' : ''}`} onClick={() => changer({ ...m, jours: 'tous' })}>Tous les jours</button>
            </div>
          )}
        </div>
      );
    }
    case 'nom_complet': {
      const n = v as { prenom: string; nom?: string };
      return (
        <div className="formulaire">
          <input className="saisie texte entretien-saisie" placeholder="Prénom" value={n.prenom} maxLength={40} onChange={(e) => changer({ ...n, prenom: e.target.value })} autoFocus aria-label="Prénom" />
          <input className="saisie texte entretien-saisie" placeholder="Nom (facultatif)" value={n.nom ?? ''} maxLength={60} onChange={(e) => changer({ ...n, nom: e.target.value })} aria-label="Nom" />
        </div>
      );
    }
    case 'date_annuelle': {
      const d = v as { inconnu?: boolean; jour?: number; mois?: number; annee?: number };
      if (d.inconnu) {
        return (
          <div className="formulaire">
            <p style={{ margin: 0 }}>{vous ? 'Vous ne la connaissez pas : ce n’est pas grave.' : 'Tu ne la connais pas : ce n’est pas grave.'}</p>
            <button type="button" className="btn" onClick={() => changer({ jour: 0, mois: 0 })}>Finalement, je la connais</button>
          </div>
        );
      }
      return (
        <div className="formulaire">
          <div className="boutons">
            <select className="saisie texte entretien-saisie" value={d.jour ?? 0} onChange={(e) => changer({ ...d, jour: Number(e.target.value) })} aria-label="Jour">
              <option value={0}>Jour</option>
              {Array.from({ length: 31 }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
            </select>
            <select className="saisie texte entretien-saisie" value={d.mois ?? 0} onChange={(e) => changer({ ...d, mois: Number(e.target.value) })} aria-label="Mois">
              <option value={0}>Mois</option>
              {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <input className="saisie texte entretien-saisie" inputMode="numeric" placeholder="Année (facultatif)" value={d.annee ?? ''} maxLength={4} aria-label="Année"
            onChange={(e) => {
              const a = Number(e.target.value.replace(/\D/g, ''));
              changer({ jour: d.jour, mois: d.mois, ...(a ? { annee: a } : {}) });
            }} />
          <button type="button" className="btn" onClick={() => changer({ inconnu: true })}>Je ne sais pas</button>
        </div>
      );
    }
    case 'date': {
      const d = v as { aucune?: boolean; date?: string };
      return (
        <div className="choix-mode" role="radiogroup">
          <input className="saisie texte entretien-saisie" type="date" value={d.date ?? ''} onChange={(e) => changer({ date: e.target.value })} aria-label="Date" />
          <button type="button" className="btn" role="radio" aria-checked={!!d.aucune} onClick={() => changer({ aucune: true })}><b>Pas d’échéance</b></button>
        </div>
      );
    }
    case 'habitudes': {
      const liste = v as Habitude[];
      const maj = (i: number, x: Partial<Habitude>) => changer(liste.map((h, k) => (k === i ? { ...h, ...x } : h)));
      return (
        <div className="formulaire">
          {q.options.map((o) => {
            const i = liste.findIndex((h) => h.habitude === o.valeur);
            const h = liste[i];
            return (
              <div key={o.valeur} className={h ? 'entretien-plage' : undefined}>
                <label className="entretien-case">
                  <input type="checkbox" checked={!!h} onChange={(e) => changer(e.target.checked
                    ? [...liste, { habitude: o.valeur, frequence: 'semaine', fois: 3, moment: 'soir' }]
                    : liste.filter((x) => x.habitude !== o.valeur))} />
                  {lib(o)}
                </label>
                {h && (
                  <>
                    {o.champ === 'texte' && <input className="saisie texte entretien-saisie" placeholder="Laquelle ?" value={h.texte ?? ''} maxLength={60} onChange={(e) => maj(i, { texte: e.target.value })} />}
                    <div className="boutons">
                      <button type="button" className={`btn${h.frequence === 'jour' ? ' principal' : ''}`} onClick={() => maj(i, { frequence: 'jour' })}>Chaque jour</button>
                      <button type="button" className={`btn${h.frequence === 'semaine' ? ' principal' : ''}`} onClick={() => maj(i, { frequence: 'semaine' })}>Par semaine</button>
                    </div>
                    {h.frequence === 'semaine' && !h.jours?.length && (
                      <select className="saisie texte entretien-saisie" value={h.fois ?? 3} onChange={(e) => maj(i, { fois: Number(e.target.value) })} aria-label="Fois par semaine">
                        {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} fois par semaine</option>)}
                      </select>
                    )}
                    {h.frequence === 'semaine' && (
                      <>
                        <span className="muted" style={{ fontSize: 12 }}>Des jours précis ? (sinon, je les répartis)</span>
                        <ChoixJours valeur={h.jours ?? []} changer={(jours) => maj(i, { jours })} />
                      </>
                    )}
                    <div className="jours-choix moments">
                      {MOMENTS.map(([m, l]) => <button key={m} type="button" aria-pressed={h.moment === m} onClick={() => maj(i, { moment: m })}>{l}</button>)}
                    </div>
                    {h.moment === 'heure' && <input className="saisie texte entretien-saisie" type="time" value={h.heure ?? ''} onChange={(e) => maj(i, { heure: e.target.value })} aria-label="Heure" />}
                  </>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    case 'animaux': {
      const a = v as { nom: string; espece: string }[];
      const maj = (i: number, x: Partial<{ nom: string; espece: string }>) => changer(a.map((y, k) => (k === i ? { ...y, ...x } : y)));
      return (
        <div className="formulaire">
          {a.length === 0 && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Pas d’animal ? « Suivant » suffit.</p>}
          {a.map((x, i) => (
            <div key={i} className="boutons">
              <input className="saisie texte entretien-saisie" placeholder="Son nom" value={x.nom} maxLength={40} onChange={(e) => maj(i, { nom: e.target.value })} aria-label="Nom" />
              <input className="saisie texte entretien-saisie" placeholder="Chien, chat…" value={x.espece} maxLength={40} onChange={(e) => maj(i, { espece: e.target.value })} aria-label="Espèce" />
              <button type="button" className="btn petit" onClick={() => changer(a.filter((_, k) => k !== i))} aria-label="Retirer">×</button>
            </div>
          ))}
          {a.length < 10 && <button type="button" className="btn" onClick={() => changer([...a, { nom: '', espece: '' }])}>Ajouter un animal</button>}
        </div>
      );
    }
    case 'taches':
    case 'echeances': {
      const cle = q.type === 'taches' ? 'tache' : 'echeance';
      const liste = v as Record<string, unknown>[];
      const maj = (i: number, x: Record<string, unknown>) => changer(liste.map((y, k) => (k === i ? { ...y, ...x } : y)));
      return (
        <div className="formulaire">
          {q.options.map((o) => {
            const i = liste.findIndex((x) => x[cle] === o.valeur);
            const x = liste[i];
            return (
              <div key={o.valeur} className={x ? 'entretien-plage' : undefined}>
                <label className="entretien-case">
                  <input type="checkbox" checked={!!x} onChange={(e) => changer(e.target.checked
                    ? [...liste, q.type === 'taches' ? { tache: o.valeur, jours: [] } : { echeance: o.valeur, mois: 0 }]
                    : liste.filter((y) => y[cle] !== o.valeur))} />
                  {lib(o)}
                </label>
                {x && o.champ === 'texte' && <input className="saisie texte entretien-saisie" placeholder="Laquelle ?" value={(x['texte'] as string) ?? ''} maxLength={60} onChange={(e) => maj(i, { texte: e.target.value })} />}
                {x && q.type === 'taches' && (
                  <>
                    <ChoixJours valeur={x['jours'] as string[]} changer={(jours) => maj(i, { jours })} />
                    <input className="saisie texte entretien-saisie" type="time" value={(x['heure'] as string) ?? ''} onChange={(e) => maj(i, e.target.value ? { heure: e.target.value } : { heure: undefined })} aria-label="Heure (facultatif)" />
                  </>
                )}
                {x && q.type === 'echeances' && (
                  <div className="boutons">
                    <select className="saisie texte entretien-saisie" value={(x['mois'] as number) ?? 0} onChange={(e) => maj(i, { mois: Number(e.target.value) })} aria-label="Mois">
                      <option value={0}>Mois</option>
                      {MOIS.map((m, k) => <option key={m} value={k + 1}>{m}</option>)}
                    </select>
                    <select className="saisie texte entretien-saisie" value={(x['jour'] as number) ?? 0} onChange={(e) => maj(i, Number(e.target.value) ? { jour: Number(e.target.value) } : { jour: undefined })} aria-label="Jour (facultatif)">
                      <option value={0}>Jour ?</option>
                      {Array.from({ length: 31 }, (_, k) => <option key={k} value={k + 1}>{k + 1}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    case 'plages': {
      const p = v as { jours: string[]; de: string; a: string }[];
      const maj = (i: number, x: Partial<(typeof p)[number]>) => changer(p.map((y, k) => (k === i ? { ...y, ...x } : y)));
      return (
        <div className="formulaire">
          {p.map((x, i) => (
            <div key={i} className="entretien-plage">
              <ChoixJours valeur={x.jours} changer={(jours) => maj(i, { jours })} />
              <div className="boutons">
                <input className="saisie texte entretien-saisie" type="time" value={x.de} onChange={(e) => maj(i, { de: e.target.value })} aria-label="De" />
                <input className="saisie texte entretien-saisie" type="time" value={x.a} onChange={(e) => maj(i, { a: e.target.value })} aria-label="À" />
                <button type="button" className="btn petit" onClick={() => changer(p.filter((_, k) => k !== i))}>Retirer</button>
              </div>
            </div>
          ))}
          {p.length < 6 && <button type="button" className="btn" onClick={() => changer([...p, { jours: ['lu', 'ma', 'me', 'je', 've'], de: '19:00', a: '20:30' }])}>Ajouter un moment</button>}
        </div>
      );
    }
  }
}

// --- un thème ----------------------------------------------------------------------------------------------------------

export function ThemeEcran({ numero, actions }: { numero: number; actions: Actions }) {
  const [t, setT] = useState<ThemeEntretien | null>(null);
  const [rang, setRang] = useState(0);
  const [reponses, setReponses] = useState<Record<string, Valeur>>({});
  const [passees, setPassees] = useState<string[]>([]);
  const [lignes, setLignes] = useState<LigneRetenue[] | null>(null);
  const [decochees, setDecochees] = useState<string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  // Un thème en boucle (les proches, les projets) : les éléments déjà saisis, l'écran « Ajouter quelqu'un d'autre ? »,
  // puis les questions qui suivent la boucle (les habitudes après les projets).
  const [proches, setProches] = useState<Record<string, Valeur>[]>([]);
  const [entre, setEntre] = useState(false);
  const [phase, setPhase] = useState<'boucle' | 'suite'>('boucle');
  const [relecture, setRelecture] = useState(false);
  // Les lectures par l'IA lancées pendant la saisie (une par élément de la boucle, une par question hors boucle) :
  // l'aperçu les attend.
  const lectures = useRef(new Map<number, Promise<string | null>>());
  const lecturesSuite = useRef(new Map<string, Promise<string | null>>());

  const echec = useCallback((e: unknown) => versConnexion(e, actions), [actions]);

  useEffect(() => {
    api.themeEntretien(numero).then(setT).catch(echec);
  }, [numero, echec]);

  const enBoucle = !!t?.boucle && phase === 'boucle';
  // Les questions posées : celles de la phase en cours, selon les réponses (Q2.4 seulement si des jours de travail
  // sont cochés).
  const posees = useMemo(() => {
    const toutes = (t?.questions ?? []).filter((x) => x.boucle === enBoucle);
    const valeurDe = (id: string) => {
      const autre = toutes.find((x) => x.id === id);
      return id in reponses ? reponses[id] : autre ? depart(autre, reponses) : undefined;
    };
    return toutes.filter((q) => Object.entries(q.si ?? {}).every(([id, forme]) => forme !== 'jours' || !!(valeurDe(id) as { jours?: string[] } | undefined)?.jours?.length));
  }, [t, reponses, enBoucle]);

  const q = posees[rang];
  const v = q ? (q.id in reponses ? reponses[q.id] : depart(q, reponses)) : null;
  const vous = ((reponses['Q1.3'] as { valeur?: string } | undefined)?.valeur ?? t?.registre ?? 'vous') !== 'tu';
  const prenom = (reponses['Q1.1'] as string | undefined)?.trim() || t?.prenom || '';
  const personne = (reponses['Q3.1'] as { prenom?: string } | undefined)?.prenom?.trim() || 'cette personne';
  /** Ce dont parle le texte libre, pour l'IA : le proche ou le projet en cours. */
  const sujet = (reponses['Q3.1'] as { prenom?: string } | undefined)?.prenom?.trim() || (reponses['Q4.1'] as string | undefined)?.trim() || '';
  const nomElement = (e: Record<string, Valeur>) => (e['Q3.1'] as { prenom?: string } | undefined)?.prenom ?? (e['Q4.1'] as string | undefined) ?? '';

  if (!t) return <main className="ecran centre"><p className="message muted">Chargement…</p></main>;

  const voir = async (c: { reponses?: Record<string, Valeur>; passees?: string[]; boucle?: Record<string, Valeur>[]; extractions?: Record<string, string> }) => {
    setRelecture(true);
    try {
      setLignes((await api.apercuEntretien(numero, c)).lignes);
      setDecochees([]);
    } catch (e) {
      echec(e);
    } finally {
      setRelecture(false);
    }
  };

  /** La boucle, chaque personne avec l'identifiant de sa lecture par l'IA, une fois celle-ci lancée. */
  const avecLectures = (liste: Record<string, Valeur>[]) => Promise.all(liste.map(async (p, i) => {
    const id = await (lectures.current.get(i) ?? Promise.resolve(null));
    return id ? { ...p, extraction: id } : p;
  }));

  /** Les lectures des questions hors boucle (Q5.5), par question. */
  const extractionsSuite = async () => {
    const ids: Record<string, string> = {};
    for (const [id, p] of lecturesSuite.current) {
      const x = await p;
      if (x) ids[id] = x;
    }
    return ids;
  };

  /** Tout ce que l'aperçu et l'enregistrement reçoivent. */
  const corps = async (r: Record<string, Valeur>, p: string[]) => ({
    reponses: r, passees: p, extractions: await extractionsSuite(), ...(t.boucle ? { boucle: await avecLectures(proches) } : {}),
  });

  const suite = !!t.boucle && t.questions.some((x) => !x.boucle);

  const avancer = async (passe: boolean) => {
    if (!q) return;
    const r = passe ? Object.fromEntries(Object.entries(reponses).filter(([k]) => k !== q.id)) : { ...reponses, [q.id]: v };
    const p = passe ? [...new Set([...passees, q.id])] : passees.filter((x) => x !== q.id);
    // Un texte libre lu par l'IA : la lecture part tout de suite, en arrière-plan, pendant que la saisie continue.
    if (q.ia && !passe && typeof v === 'string' && v.trim()) {
      const lecture = api.extraireEntretien(q.id, v.trim(), sujet).then((x) => x.id).catch(() => null);
      if (enBoucle) lectures.current.set(proches.length, lecture);
      else lecturesSuite.current.set(q.id, lecture);
    }
    setReponses(r);
    setPassees(p);
    if (rang + 1 < posees.length) {
      setRang(rang + 1);
      return;
    }
    if (enBoucle) {
      setProches([...proches, r]);
      setReponses({});
      setRang(0);
      setEntre(true);
      return;
    }
    await voir(await corps(r, p));
  };

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const tab = await api.enregistrerEntretien(numero, { ...(await corps(reponses, passees)), decochees });
      actions.toast({ texte: `C’est enregistré. ${tab.assistant} vous connaît à ${tab.score} %.` });
      await actions.recharger();
      window.location.hash = '#/entretien';
    } catch (e) {
      echec(e);
      setEnvoi(false);
    }
  };

  if (relecture) {
    return (
      <main className="ecran centre">
        <p className="message muted">Je relis ce que {vous ? 'vous m’avez' : 'tu m’as'} dit…</p>
        <p className="muted" style={{ textAlign: 'center', fontSize: 13 }}>Quelques secondes, parfois une minute.</p>
      </main>
    );
  }

  if (lignes) {
    return (
      <main className="ecran">
        <EnTeteRetour titre={t.titre} retour="#/entretien" />
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Voici ce que j’ai retenu</span>
          {lignes.length === 0 && <p className="muted" style={{ margin: 0 }}>Rien à retenir : toutes les questions ont été passées.</p>}
          {lignes.map((l) => (
            <label key={l.cle} className="entretien-case ligne-retenue">
              <input type="checkbox" checked={!decochees.includes(l.cle)} onChange={(e) => setDecochees(e.target.checked ? decochees.filter((x) => x !== l.cle) : [...decochees, l.cle])} />
              <span>{l.texte}</span>
            </label>
          ))}
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>Ce qui est décoché n’est pas enregistré. Visible par vous seul.</p>
        </Fenetre>
        <div className="boutons">
          <button className="btn" onClick={() => { setLignes(null); if (enBoucle) setEntre(true); else setRang(Math.max(0, posees.length - 1)); }}>Revenir en arrière</button>
          <button className="btn principal" onClick={() => void enregistrer()} disabled={envoi}>Tout enregistrer</button>
        </div>
      </main>
    );
  }

  if (enBoucle && entre) {
    const terminer = () => {
      // Après la boucle, les questions qui la suivent (les habitudes après les projets) ; sinon l'aperçu.
      if (suite) {
        setPhase('suite');
        setEntre(false);
        setRang(0);
        setReponses({});
      } else void avecLectures(proches).then((boucle) => voir({ boucle }));
    };
    return (
      <main className="ecran">
        <EnTeteRetour titre={t.titre} retour="#/entretien" />
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>{proches.length ? 'C’est noté' : t.boucle === 'projet' ? 'Aucun projet ?' : 'Personne à ajouter ?'}</span>
          {proches.map((p, i) => <p key={i} style={{ margin: 0 }}>{nomElement(p)}</p>)}
          {!proches.length && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Ce thème pourra être repris quand vous voudrez.</p>}
        </Fenetre>
        <div className="boutons">
          <button className="btn" onClick={() => setEntre(false)}>{t.boucle === 'projet' ? 'Ajouter un projet' : 'Ajouter quelqu’un'}</button>
          <button className="btn principal" onClick={terminer}>{suite ? 'Continuer' : 'Terminer'}</button>
        </div>
      </main>
    );
  }

  if (!q) return null;
  const texte = (vous ? q.texte : q.texte_tu).replace('{prenom}', prenom).replace('{personne}', personne).replace('{assistant}', t.assistant);
  const premiere = rang === 0 && proches.length === 0 && (enBoucle || !t.boucle);
  return (
    <main className="ecran">
      <EnTeteRetour titre={t.titre} retour="#/entretien" droite={enBoucle ? `n° ${proches.length + 1}` : `${rang + 1}/${posees.length}`} />
      {premiere && <p className="muted" style={{ margin: 0, textAlign: 'center' }}>{vous ? t.intro : t.intro_tu}</p>}
      {premiere && t.connus.length > 0 && (
        <p className="muted" style={{ margin: 0, textAlign: 'center', fontSize: 13 }}>Je connais déjà : {t.connus.slice(0, 8).join(', ')}{t.connus.length > 8 ? '…' : ''}</p>
      )}
      <Fenetre>
        <p className="entretien-question">{texte}</p>
        {(vous ? q.aide : q.aide_tu ?? q.aide) && <p className="muted" style={{ margin: 0, fontSize: 13 }}>{vous ? q.aide : q.aide_tu ?? q.aide}</p>}
        <Champ key={`${proches.length}-${q.id}`} q={q} v={v} vous={vous} prenom={prenom} changer={(x) => setReponses({ ...reponses, [q.id]: x })} />
      </Fenetre>
      <div className="boutons">
        {rang > 0 && <button className="btn" onClick={() => setRang(rang - 1)} aria-label="Question précédente"><Icone nom="retour" /></button>}
        {rang === 0 && enBoucle && (
          <button className="btn" onClick={() => { setReponses({}); setEntre(true); }}>
            {t.boucle === 'projet' ? (proches.length ? 'Plus de projet' : 'Aucun') : proches.length ? 'Plus personne' : 'Personne'}
          </button>
        )}
        {!q.obligatoire && <button className="btn" onClick={() => void avancer(true)}>Passer</button>}
        <button className="btn principal" onClick={() => void avancer(false)} disabled={!complete(q, v)}>Suivant</button>
      </div>
    </main>
  );
}

// --- la question du jour (lot 3.5d) -----------------------------------------------------------------------------------

/** La carte de l'accueil : la question du jour, « Répondre » ou « Pas maintenant ». Rien s'il n'y en a pas. */
export function CarteQuestionDuJour({ actions }: { actions: Actions }) {
  const [q, setQ] = useState<QuestionDuJour | null>(null);
  useEffect(() => {
    api.questionDuJour().then((r) => setQ(r.question)).catch(() => setQ(null));
  }, []);
  if (!q) return null;
  const ignorer = () => {
    setQ(null);
    api.ignorerQuestionDuJour().catch((e: unknown) => versConnexion(e, actions));
  };
  return (
    <Fenetre className="question-du-jour">
      <span className="mono muted" style={{ fontSize: 11 }}>QUESTION DU JOUR</span>
      <p style={{ margin: 0, fontWeight: 600 }}>{q.texte}</p>
      <div className="boutons">
        <button className="btn" onClick={ignorer}>Pas maintenant</button>
        <a className="btn principal entretien-suggestion" href={q.genre === 'theme' ? `#/entretien/${q.numero}` : '#/question-du-jour'}>Répondre</a>
      </div>
    </Fenetre>
  );
}

/** Répondre à la question du jour : la question seule, puis « Voici ce que j'ai retenu ». */
export function QuestionDuJourEcran({ actions }: { actions: Actions }) {
  const [donnees, setDonnees] = useState<{ q: Extract<QuestionDuJour, { genre: 'question' }>; vous: boolean; prenom: string } | null | 'aucune'>(null);
  const [v, setV] = useState<Valeur>(null);
  const [lignes, setLignes] = useState<LigneRetenue[] | null>(null);
  const [decochees, setDecochees] = useState<string[]>([]);
  const [lecture, setLecture] = useState<string | undefined>(undefined);
  const [attente, setAttente] = useState(false);

  useEffect(() => {
    api.questionDuJour().then((r) => {
      if (r.question?.genre !== 'question') {
        setDonnees('aucune');
        return;
      }
      setDonnees({ q: r.question, vous: r.registre !== 'tu', prenom: r.prenom ?? '' });
      setV(depart(r.question.question, {}));
    }).catch((e: unknown) => versConnexion(e, actions));
  }, [actions]);

  if (donnees === null) return <main className="ecran centre"><p className="message muted">Chargement…</p></main>;
  if (donnees === 'aucune') {
    return (
      <main className="ecran centre">
        <p className="message muted">Pas de question aujourd’hui.</p>
        <a className="btn" href="#/">Retour</a>
      </main>
    );
  }
  const { q, vous, prenom } = donnees;

  const voir = async () => {
    setAttente(true);
    try {
      // Un texte libre lu par l'IA (Q5.5) : la lecture d'abord, l'aperçu l'attend.
      const id = q.question.ia && typeof v === 'string' && v.trim() ? (await api.extraireEntretien(q.id, v.trim(), '')).id : undefined;
      setLecture(id);
      setLignes((await api.apercuQuestionDuJour(v, id)).lignes);
    } catch (e) {
      versConnexion(e, actions);
    } finally {
      setAttente(false);
    }
  };
  const enregistrer = async () => {
    try {
      const t = await api.enregistrerQuestionDuJour(v, lecture, decochees);
      actions.toast({ texte: `Merci. ${t.assistant} vous connaît à ${t.score} %.` });
      window.location.hash = '#/';
    } catch (e) {
      versConnexion(e, actions);
    }
  };

  if (attente) return <main className="ecran centre"><p className="message muted">Je relis ce que {vous ? 'vous m’avez' : 'tu m’as'} dit…</p></main>;
  if (lignes) {
    return (
      <main className="ecran">
        <EnTeteRetour titre="Question du jour" retour="#/" />
        <Fenetre>
          <span className="display" style={{ fontWeight: 500 }}>Voici ce que j’ai retenu</span>
          {lignes.length === 0 && <p className="muted" style={{ margin: 0 }}>Rien à retenir.</p>}
          {lignes.map((l) => (
            <label key={l.cle} className="entretien-case ligne-retenue">
              <input type="checkbox" checked={!decochees.includes(l.cle)} onChange={(e) => setDecochees(e.target.checked ? decochees.filter((x) => x !== l.cle) : [...decochees, l.cle])} />
              <span>{l.texte}</span>
            </label>
          ))}
        </Fenetre>
        <div className="boutons">
          <button className="btn" onClick={() => setLignes(null)}>Revenir en arrière</button>
          <button className="btn principal" onClick={() => void enregistrer()}>Enregistrer</button>
        </div>
      </main>
    );
  }
  const question = q.question;
  return (
    <main className="ecran">
      <EnTeteRetour titre="Question du jour" retour="#/" />
      <Fenetre>
        <p className="entretien-question">{q.texte}</p>
        {(vous ? question.aide : question.aide_tu ?? question.aide) && <p className="muted" style={{ margin: 0, fontSize: 13 }}>{vous ? question.aide : question.aide_tu ?? question.aide}</p>}
        <Champ q={question} v={v} vous={vous} prenom={prenom} changer={setV} />
      </Fenetre>
      <div className="boutons">
        <button className="btn" onClick={() => void api.ignorerQuestionDuJour().then(() => { window.location.hash = '#/'; }).catch((e: unknown) => versConnexion(e, actions))}>Pas maintenant</button>
        <button className="btn principal" onClick={() => void voir()} disabled={!complete(question, v)}>Suivant</button>
      </div>
    </main>
  );
}
